import React, { useEffect, useState } from "react";
import axios from "axios";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
	Box,
	CircularProgress,
	Typography,
	TextField,
	Grid,
} from "@mui/material";
import { FiInbox } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { parseISO, isAfter, isBefore, isSameDay } from "date-fns";
import { API_URL } from '../config/api';
import BRANDING from "../config/branding";

const UploadedFilesTable = () => {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchText, setSearchText] = useState("");
	const [fromDate, setFromDate] = useState(null);
	const [toDate, setToDate] = useState(null);

	const navigate = useNavigate();

	useEffect(() => {
		const fetchData = async () => {
			try {
				const token = localStorage.getItem("token");
				const res = await axios.get(`${API_URL}/upload.php`, {
					headers: { Authorization: `Bearer ${token}` },
				});

				if (res.data.status && Array.isArray(res.data.data)) {
					const formatted = res.data.data.map((file) => {
						const totalCount = Number(file.total_count) || 0;
						const totalDistinct = Number(file.total_distinct) || 0;
						const count = Number(file.count) || 0;

						let statusText = "Unknown";
						if (file.status === "4") {
							statusText = "Uploaded";
						} else if (file.status === "0" || file.status === "1") {
							statusText = "Processing";
						} else if (file.status === "2") {
							statusText = "Completed";
						} else if (file.status === "3" || file.status === "5") {
							statusText = "Failed";
						}

						return {
							id: file.id,
							...file,
							total_count: totalCount,
							total_distinct: totalDistinct,
							status: statusText,
							count: count > 0 ? count : 1,
							duplicates: Math.max(0, totalCount - totalDistinct),
						};
					});

					setRows(formatted);
				} else {
					setRows([]);
				}
			} catch (error) {
				console.error("Failed to fetch file data:", error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	const filteredRows = rows.filter((row) => {
		const nameMatch = row.file_name
			?.toLowerCase()
			.includes(searchText.toLowerCase());

		// Date filtering logic
		if (!fromDate && !toDate) return nameMatch;

		const rowDate = row.created_at ? parseISO(row.created_at) : null;
		if (!rowDate) return nameMatch;

		let dateMatch = true;

		// Check if row date is within the date range
		if (fromDate && toDate) {
			// Both dates selected - check if row date is between them (inclusive)
			dateMatch =
				(isSameDay(rowDate, fromDate) || isAfter(rowDate, fromDate)) &&
				(isSameDay(rowDate, toDate) || isBefore(rowDate, toDate));
		} else if (fromDate) {
			// Only from date selected - check if row date is on or after from date
			dateMatch = isSameDay(rowDate, fromDate) || isAfter(rowDate, fromDate);
		} else if (toDate) {
			// Only to date selected - check if row date is on or before to date
			dateMatch = isSameDay(rowDate, toDate) || isBefore(rowDate, toDate);
		}

		return nameMatch && dateMatch;
	});

	const handleRowClick = (params) => {
		const fileDetails = params.row;
		if (fileDetails.status !== "Processing") {
			navigate(`/dashboard/file-summary/${fileDetails.batch_id}`, {
				state: { file: fileDetails },
			});
		}
	};

	const columns = [
		{ field: "id", headerName: "ID", width: 70 },
		{ field: "file_name", headerName: "File Name", width: 200 },
		{ field: "batch_id", headerName: "Batch ID", width: 170 },
		{ field: "type", headerName: "Type", width: 100 },
		{ field: "msg_cat", headerName: "Category", width: 120 },
		{
			field: "total_count",
			headerName: "Total Count",
			width: 120,
			type: "number",
		},
		{
			field: "total_distinct",
			headerName: "Total Distinct",
			width: 130,
			type: "number",
		},
		{
			field: "duplicates",
			headerName: "Duplicates",
			width: 100,
			type: "number",
		},
		{
			field: "status",
			headerName: "Status",
			width: 120,
			renderCell: (params) => {
				let colorClass = "font-semibold";
				if (params.value === "Completed") {
					colorClass = "text-green-700 font-semibold";
				} else if (params.value === "Processing") {
					colorClass = "text-yellow-700 font-semibold";
				} else if (params.value === "Failed") {
					colorClass = "text-red-700 font-semibold";
				} else if (params.value === "Uploaded") {
					colorClass = "text-red-500 font-semibold";
				}
				return <span className={colorClass}>{params.value}</span>;
			},
		},
		{ field: "cost_cntr", headerName: "Cost Center", width: 180 },
		{ field: "created_at", headerName: "Created At", width: 180 },
		{ field: "message", headerName: "Message", width: 250 },
		{ field: "schedule_time", headerName: "Scheduled At", width: 180 },
		{ field: "uploaded_by", headerName: "Uploaded By", width: 180 },
		{ field: "approved_by", headerName: "Approved By", width: 180 },
	];

	return (
		<Box
			sx={{
				height: 600,
				width: "100%",
				padding: 3,
				backgroundColor: "#ffffff", // Light red background
				borderRadius: 3,
				boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
				border: "1px solid #E5E7EB", // Light red border
			}}
		>
			<Typography
					variant='h4'
					gutterBottom
					sx={{
						color: BRANDING.primaryColor,
						fontWeight: 700,
						mb: 1,
						textAlign: "center",
					}}
				>
					Sent Communications
				</Typography>

				<Typography
					variant='body1'
					sx={{
						textAlign: "center",
						color: "#64748B",
						mb: 4,
					}}
				>
					View and manage communication delivery history
				</Typography>

			{/* Search and Date Filters */}
			<Box
				sx={{
					mb: 3,
					p: 2,
					backgroundColor: "#F8FAFC",
					borderRadius: 2,
					border: "1px solid #E2E8F0",
					boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
				}}
			>
				<Grid container spacing={2} alignItems='center'>
					<Grid item xs={12} md={6}>
						<TextField
							label='Search by File Name'
							variant='outlined'
							size='small'
							fullWidth
							value={searchText}
							onChange={(e) => setSearchText(e.target.value)}
							sx={{
								"& .MuiOutlinedInput-root": {
									borderRadius: 2,
									backgroundColor: "#ffffff", // Light red background
									"&:hover .MuiOutlinedInput-notchedOutline": {
										borderColor: BRANDING.primaryColor, // Red-400
									},
									"&.Mui-focused fieldset": {
										borderColor: BRANDING.primaryColor, // Red-600
									},
								},
								"& .MuiInputLabel-root.Mui-focused": {
									color: BRANDING.primaryColor, // Red-600
								},
							}}
						/>
					</Grid>

					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<Grid item xs={12} sm={6} md={3}>
							<DatePicker
								label='From Date'
								value={fromDate}
								onChange={(newValue) => setFromDate(newValue)}
								slotProps={{
									textField: {
										size: "small",
										fullWidth: true,
										sx: {
											"& .MuiOutlinedInput-root": {
												borderRadius: 2,
												backgroundColor: "#ffffff", // Light red background
												"&:hover .MuiOutlinedInput-notchedOutline": {
													borderColor: BRANDING.primaryColor, // Red-400
												},
												"&.Mui-focused fieldset": {
													borderColor: BRANDING.primaryColor, // Red-600
												},
											},
											"& .MuiInputLabel-root.Mui-focused": {
												color: "#60A5FA", // Red-600
											},
										},
									},
								}}
							/>
						</Grid>

						<Grid item xs={12} sm={6} md={3}>
							<DatePicker
								label='To Date'
								value={toDate}
								onChange={(newValue) => setToDate(newValue)}
								minDate={fromDate} // Prevent selecting a to date before from date
								slotProps={{
									textField: {
										size: "small",
										fullWidth: true,
										sx: {
											"& .MuiOutlinedInput-root": {
												borderRadius: 2,
												backgroundColor: "#ffffff", // Light red background
												"&:hover .MuiOutlinedInput-notchedOutline": {
													borderColor: BRANDING.primaryColor, // Red-400
												},
												"&.Mui-focused fieldset": {
													borderColor: BRANDING.primaryColor, // Red-600
												},
											},
											"& .MuiInputLabel-root.Mui-focused": {
												color:  BRANDING.primaryColor, // Red-600
											},
										},
									},
								}}
							/>
						</Grid>
					</LocalizationProvider>
				</Grid>

				{/* Filter Summary */}
				{(fromDate || toDate || searchText) && (
					<Box
						sx={{ mt: 2, p: 1.5, backgroundColor: "#FEF2F2", borderRadius: 1 }}
					>
						<Typography
							variant='body2'
							sx={{ color: BRANDING.primaryColor, fontWeight: "500" }}
						>
							Active Filters:
							{searchText && ` File Name: "${searchText}"`}
							{fromDate && ` From: ${fromDate.toLocaleDateString()}`}
							{toDate && ` To: ${toDate.toLocaleDateString()}`}{" "}
							<Typography
								component='span'
								sx={{
									color: "#dc2626",
									cursor: "pointer",
									textDecoration: "underline",
									ml: 1,
								}}
								onClick={() => {
									setSearchText("");
									setFromDate(null);
									setToDate(null);
								}}
							>
								Clear All
							</Typography>
						</Typography>
					</Box>
				)}
			</Box>

			{loading ? (
				<Box className='flex items-center justify-center mt-16'>
					<CircularProgress sx={{ color: BRANDING.primaryColor }} />
				</Box>
			) : filteredRows.length === 0 ? (
				<Box className='text-center text-gray-500 mt-20'>
					<FiInbox className='text-6xl mx-auto mb-2 text-red-500' />
					<Typography variant='subtitle1' sx={{ color: BRANDING.primaryColor }}>
						No matching files found
					</Typography>
					{(fromDate || toDate || searchText) && (
						<Typography variant='body2' sx={{ color: "#6b7280", mt: 1 }}>
							Adjust your search criteria and try again
						</Typography>
					)}
				</Box>
			) : (
				<DataGrid
    rows={filteredRows}
    columns={columns}
    pageSizeOptions={[10, 20, 50]}
    initialState={{
        pagination: {
            paginationModel: { pageSize: 10 },
        },
        sorting: {
            sortModel: [{ field: "created_at", sort: "desc" }],
        },
    }}
    slots={{ toolbar: GridToolbar }}
    slotProps={{
        toolbar: {
            showQuickFilter: true,
            sx: {
                "& .MuiButton-root": {
                    color: BRANDING.primaryColor,
                },
                "& .MuiInputBase-root": {
                    borderRadius: 2,
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#60A5FA",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1E2F97",
                    },
                },
            },
        },
    }}
    onRowClick={handleRowClick}
    sx={{
        cursor: "pointer",
        backgroundColor: "white",
        border: "1px solid #E2E8F0",
        borderRadius: 2,

        "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#EEF4FF",
            color: BRANDING.primaryColor,
            fontWeight: "bold",
            borderBottom: "2px solid #E2E8F0",
        },

        "& .MuiDataGrid-row": {
            "&:hover": {
                backgroundColor: "#F8FAFC",
            },

            "&.Mui-selected": {
                backgroundColor: "#DBEAFE",

                "&:hover": {
                    backgroundColor: "#BFDBFE",
                },
            },
        },

        "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #E2E8F0",
        },

        "& .MuiDataGrid-footerContainer": {
            backgroundColor: "#F8FAFC",
            borderTop: "1px solid #E2E8F0",
        },

        "& .MuiTablePagination-root": {
            color: BRANDING.primaryColor,
        },

        "& .MuiIconButton-root": {
            color: BRANDING.primaryColor,
        },
    }}
/>
			)}
		</Box>
	);
};

export default UploadedFilesTable;
